<template>
    <div class="user camp-member-page">
        <div class="headerbg">
            <b-container class="d-flex align-items-center justify-content-between">
                <h1>{{ title }}</h1>
                <b-button variant="outline-secondary" size="sm" @click="logout">登出</b-button>
            </b-container>
        </div>
        <b-container fluid="xl" class="pb-5">
            <b-row>
                <b-col cols="12" md="2" class="member-sidebar-col">
                    <nacUser></nacUser>
                </b-col>

                <b-col cols="12" md="10" class="member-content-col">
                    <div v-if="loading" class="text-center py-5">
                        <b-spinner></b-spinner>
                    </div>

                    <b-card v-else no-body class="relation-form-card">
                        <b-alert v-if="showErrors && errorCount > 0" show variant="danger" class="m-3 mb-0">
                            尚有 {{ errorCount }} 個欄位需要修正，請查看標示紅點的分頁
                        </b-alert>
                        <b-alert v-if="saveError" show variant="danger" class="m-3 mb-0">{{ saveError }}</b-alert>

                        <b-tabs v-model="activeTab" class="relation-tabs" nav-class="relation-tabs-nav" content-class="relation-tabs-content">
                            <!-- 基本資料 -->
                            <b-tab>
                                <template #title>基本資料<span v-if="tabHasError(0)" class="tab-dot"></span></template>
                                <b-row>
                                    <b-col cols="12" md="6" lg="4">
                                        <b-form-group>
                                            <template #label>姓名 <span class="text-danger">*</span></template>
                                            <b-form-input v-model="form.Name" placeholder="姓名" :state="stateOf('Name')" />
                                            <b-form-invalid-feedback :state="stateOf('Name')">{{ errors.Name }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="6" lg="4">
                                        <b-form-group>
                                            <template #label>性別 <span class="text-danger">*</span></template>
                                            <b-form-radio-group v-model="form.Gender" buttons class="relation-toggle">
                                                <b-form-radio :value="1">男</b-form-radio>
                                                <b-form-radio :value="2">女</b-form-radio>
                                            </b-form-radio-group>
                                            <b-form-invalid-feedback :state="stateOf('Gender')">{{ errors.Gender }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="6" lg="4">
                                        <b-form-group>
                                            <template #label>出生日期 <span class="text-danger">*</span></template>
                                            <b-form-input v-model="form.Birthday" type="date" :state="stateOf('Birthday')" />
                                            <b-form-invalid-feedback :state="stateOf('Birthday')">{{ errors.Birthday }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="6" md="4">
                                        <b-form-group label="英文姓氏">
                                            <b-form-input v-model="form.EnglishLastName" placeholder="選填" maxlength="255" :state="stateOf('EnglishLastName')" />
                                            <b-form-invalid-feedback :state="stateOf('EnglishLastName')">{{ errors.EnglishLastName }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="6" md="4">
                                        <b-form-group label="英文名字">
                                            <b-form-input v-model="form.EnglishFirstName" placeholder="選填" maxlength="255" :state="stateOf('EnglishFirstName')" />
                                            <b-form-invalid-feedback :state="stateOf('EnglishFirstName')">{{ errors.EnglishFirstName }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="4">
                                        <b-form-group label="英文別名">
                                            <b-form-input v-model="form.EnglishName" placeholder="選填，例如 Steven" maxlength="255" :state="stateOf('EnglishName')" />
                                            <b-form-invalid-feedback :state="stateOf('EnglishName')">{{ errors.EnglishName }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12">
                                        <b-form-text class="mb-3">姓與名請分開填寫，與護照相同</b-form-text>
                                    </b-col>
                                    <b-col cols="12" md="6" lg="4">
                                        <b-form-group label="身分證字號">
                                            <b-form-input v-model="form.PersonalId" placeholder="身分證字號" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="6" lg="8">
                                        <b-form-group label="備註">
                                            <b-form-input v-model="form.Memo" placeholder="無則留空" />
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <!-- 就學資料 -->
                            <b-tab>
                                <template #title>就學資料<span v-if="tabHasError(1)" class="tab-dot"></span></template>
                                <b-row>
                                    <b-col cols="12" md="6" lg="4">
                                        <b-form-group>
                                            <template #label>就讀學校 <span class="text-danger">*</span></template>
                                            <b-form-input v-model="form.School" placeholder="就讀學校" :state="stateOf('School')" />
                                            <b-form-invalid-feedback :state="stateOf('School')">{{ errors.School }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="6" lg="4">
                                        <b-form-group>
                                            <template #label>年級 <span class="text-danger">*</span></template>
                                            <b-form-select v-model="form.Grade" :options="gradeOptions" :state="stateOf('Grade')" />
                                            <b-form-invalid-feedback :state="stateOf('Grade')">{{ errors.Grade }}</b-form-invalid-feedback>
                                            <b-form-text v-if="!gradeSummerNotice" class="text-muted">請依新學年度為主</b-form-text>
                                            <b-form-text v-if="gradeSummerNotice" class="text-danger">{{ gradeSummerNotice }}</b-form-text>
                                            <b-form-text v-if="gradeBirthdayMismatch" class="text-warning">
                                                {{ gradeBirthdayMismatchMessage }}
                                            </b-form-text>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="6" lg="4" v-if="showGradeMemo">
                                        <b-form-group>
                                            <template #label>年級備註 <span class="text-danger">*</span></template>
                                            <b-form-input v-model="form.GradeMemo" placeholder="請說明目前就學狀況" :state="stateOf('GradeMemo')" />
                                            <b-form-invalid-feedback :state="stateOf('GradeMemo')">{{ errors.GradeMemo }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <!-- 緊急聯絡 -->
                            <b-tab>
                                <template #title>緊急聯絡<span v-if="tabHasError(2)" class="tab-dot"></span></template>
                                <b-row>
                                    <b-col cols="12" md="4">
                                        <b-form-group label="緊急聯絡人">
                                            <b-form-input v-model="form.EmergencyContact" placeholder="姓名" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="4">
                                        <b-form-group label="關係">
                                            <b-form-input v-model="form.EmergencyRelation" placeholder="例：父、母" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="4">
                                        <b-form-group label="緊急聯絡電話">
                                            <b-form-input v-model="form.EmergencyPhone" placeholder="電話" />
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <!-- 健康與飲食 -->
                            <b-tab>
                                <template #title>健康與飲食<span v-if="tabHasError(3)" class="tab-dot"></span></template>
                                <b-row>
                                    <b-col cols="12" md="6" lg="3">
                                        <b-form-group label="飲食">
                                            <b-form-radio-group v-model="form.IsVegetarian" buttons class="relation-toggle">
                                                <b-form-radio :value="false">葷</b-form-radio>
                                                <b-form-radio :value="true">素</b-form-radio>
                                            </b-form-radio-group>
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="6" lg="3">
                                        <b-form-group label="飲食備註">
                                            <b-form-input v-model="form.DietMemo" placeholder="無則留空" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="6" lg="3">
                                        <b-form-group label="過敏史">
                                            <b-form-input v-model="form.Allergy" placeholder="無則留空" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="6" lg="3">
                                        <b-form-group label="醫療備註">
                                            <b-form-input v-model="form.MedicalMemo" placeholder="無則留空" />
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                            </b-tab>
                        </b-tabs>

                        <div class="relation-actions">
                            <span class="relation-required-hint"><span class="text-danger">*</span> 為必填欄位</span>
                            <b-button variant="secondary" class="relation-actions-cancel" :disabled="saving" @click="cancel">取消</b-button>
                            <b-button variant="primary" class="relation-actions-save" :disabled="saving" @click="save">
                                {{ saving ? '儲存中...' : '儲存' }}
                            </b-button>
                        </div>
                    </b-card>
                    <!-- 手機版底部固定操作列的佔位，避免遮住最後一個欄位 -->
                    <div class="relation-actions-spacer"></div>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
import nacUser from '@/components/nacUser.vue';
import {
    apiGetUserRelation,
    apiCreateUserRelation,
    apiUpdateUserRelation,
} from '@/api/API.js';
import {
    GradeOptions,
    GRADE_BIRTHDAY_MISMATCH_MESSAGE,
    getGradeSummerNotice,
    validateFamilyMemberFields,
    validateEnglishNameParts,
    isGradeBirthdayMismatch,
    isOtherGrade,
} from '@/utils/campEnums.js';

const createDefaultRelationForm = () => ({
    Id: null,
    Name: '',
    EnglishLastName: '',
    EnglishFirstName: '',
    EnglishName: '',
    Gender: null,
    PersonalId: '',
    Birthday: '',
    School: '',
    Grade: null,
    GradeMemo: '',
    EmergencyContact: '',
    EmergencyRelation: '',
    EmergencyPhone: '',
    IsVegetarian: false,
    DietMemo: '',
    Allergy: '',
    MedicalMemo: '',
    Memo: '',
});

// 欄位所在分頁：0 基本資料、1 就學資料、2 緊急聯絡、3 健康與飲食
const FIELD_TAB = {
    Name: 0,
    Gender: 0,
    Birthday: 0,
    EnglishLastName: 0,
    EnglishFirstName: 0,
    EnglishName: 0,
    School: 1,
    Grade: 1,
    GradeMemo: 1,
};

const isBlank = (v) => v === null || v === undefined || v === '';

export default {
    name: 'camp-relation-form',
    components: {
        nacUser,
    },
    data() {
        return {
            gradeOptions: GradeOptions,
            form: createDefaultRelationForm(),
            originalName: '',
            snapshot: '',
            loading: false,
            saving: false,
            saved: false,
            showErrors: false,
            saveError: '',
            activeTab: 0,
        };
    },
    computed: {
        isEdit() {
            return !!this.$route.params.id;
        },
        title() {
            return this.isEdit ? `編輯學員：${this.originalName}` : '新增學員';
        },
        gradeSummerNotice() {
            return getGradeSummerNotice();
        },
        gradeBirthdayMismatchMessage() {
            return GRADE_BIRTHDAY_MISMATCH_MESSAGE;
        },
        gradeBirthdayMismatch() {
            return isGradeBirthdayMismatch(this.form.Grade, this.form.Birthday);
        },
        showGradeMemo() {
            return isOtherGrade(this.form.Grade);
        },
        isDirty() {
            return !!this.snapshot && JSON.stringify(this.form) !== this.snapshot;
        },
        // 逐欄錯誤：沿用 campEnums 的驗證函式與訊息（每個欄位單獨呼叫以取得欄位層級的錯誤）。
        errors() {
            const f = this.form;
            const errs = {};
            if (!String(f.Name || '').trim()) errs.Name = '姓名為必填';
            if (!f.Birthday) errs.Birthday = '生日為必填';
            if (isBlank(f.Gender)) errs.Gender = '性別為必填';
            if (!String(f.School || '').trim()) errs.School = '就讀學校為必填';
            if (isBlank(f.Grade)) errs.Grade = '年級為必填';
            else if (Number(f.Grade) === 0 && !String(f.GradeMemo || '').trim()) {
                errs.GradeMemo = '年級為其他時，年級備註為必填';
            }
            const lastNameError = validateEnglishNameParts(f.EnglishLastName, '', '');
            const firstNameError = validateEnglishNameParts('', f.EnglishFirstName, '');
            const aliasError = validateEnglishNameParts('', '', f.EnglishName);
            if (lastNameError) errs.EnglishLastName = lastNameError;
            if (firstNameError) errs.EnglishFirstName = firstNameError;
            if (aliasError) errs.EnglishName = aliasError;
            return errs;
        },
        errorCount() {
            return Object.keys(this.errors).length;
        },
    },
    mounted() {
        if (this.IsNull(this._sessionData('TokenID')) == '') {
            alert('請先登入會員');
            this._go('/');
            return;
        }
        if (this.isEdit) {
            this.loadRelation();
        } else {
            this.snapshot = JSON.stringify(this.form);
        }
    },
    beforeRouteLeave(to, from, next) {
        if (this.isDirty && !this.saved && !confirm('尚有未儲存的變更，確定要離開嗎？')) {
            next(false);
            return;
        }
        next();
    },
    methods: {
        logout() {
            const data1 = this._storeData('Header_form1');
            if (this.IsNull(data1) !== '') {
                data1.text = '學員登入';
            }
            this._sessionData('NewToken', '');
            this._sessionData('TokenID', '');
            this._sessionData('campCartCachedCount', '');
            this._sessionData('courseCartCachedCount', '');
            this._go('/');
        },
        loadRelation() {
            this.loading = true;
            apiGetUserRelation(this.$route.params.id)
                .then((res) => {
                    const item = res.data;
                    if (!item) throw new Error('not found');
                    this.form = {
                        ...createDefaultRelationForm(),
                        ...item,
                        Birthday: item.Birthday ? String(item.Birthday).slice(0, 10) : '',
                    };
                    this.originalName = item.Name || '';
                    this.snapshot = JSON.stringify(this.form);
                })
                .catch(() => {
                    alert('查無此學員資料');
                    this.saved = true; // 略過離開詢問
                    this.$router.replace('/campRelations');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        stateOf(field) {
            return this.showErrors && this.errors[field] ? false : null;
        },
        tabHasError(tabIndex) {
            if (!this.showErrors) return false;
            return Object.keys(this.errors).some((field) => FIELD_TAB[field] === tabIndex);
        },
        cancel() {
            // 有未儲存變更時由 beforeRouteLeave 詢問
            this.$router.push('/campRelations');
        },
        save() {
            const form = this.form;
            form.EnglishLastName = (form.EnglishLastName || '').trim();
            form.EnglishFirstName = (form.EnglishFirstName || '').trim();
            form.EnglishName = (form.EnglishName || '').trim();

            this.saveError = '';
            this.showErrors = true;
            if (this.errorCount > 0) {
                const firstTab = Math.min(...Object.keys(this.errors).map((field) => FIELD_TAB[field]));
                this.activeTab = firstTab;
                return;
            }

            // 與既有規則一致：欄位驗證函式必須通過（防止逐欄檢查與共用規則不同步）
            const error = validateFamilyMemberFields(form.Name, form.Birthday, form.Gender, form.School, form.Grade, form.GradeMemo)
                || validateEnglishNameParts(form.EnglishLastName, form.EnglishFirstName, form.EnglishName);
            if (error) {
                this.saveError = error;
                return;
            }

            this.saving = true;
            const api = this.isEdit ? apiUpdateUserRelation : apiCreateUserRelation;
            const { Grade: gradeRaw, Gender: genderRaw, ...rest } = form;
            const payload = {
                ...rest,
                Grade: gradeRaw !== '' && gradeRaw !== null ? parseInt(gradeRaw, 10) : null,
                Gender: genderRaw !== '' && genderRaw !== null ? parseInt(genderRaw, 10) : null,
            };
            api(payload)
                .then(() => {
                    this.saved = true;
                    this.$router.push('/campRelations');
                })
                .catch((err) => {
                    this.saveError = err?.response?.data?.error || '儲存失敗，請稍後再試';
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },
};
</script>

<style scoped>
.relation-required-hint {
    margin-right: auto;
    align-self: center;
    font-size: 0.8125rem;
    color: #6c757d;
}

/* 底線式分頁：沒有卡片標頭框，只留一條分隔線，選中項以底線標示 */
.relation-tabs >>> .relation-tabs-nav {
    padding: 0 24px;
    border-bottom: 1px solid #e3e6ea;
}

.relation-tabs >>> .relation-tabs-nav .nav-link {
    padding: 16px 16px 14px;
    margin-bottom: -1px;
    font-size: 1rem;
    color: #495057;
    background: transparent;
    border: 0;
    border-bottom: 3px solid transparent;
    border-radius: 0;
}

.relation-tabs >>> .relation-tabs-nav .nav-link:hover,
.relation-tabs >>> .relation-tabs-nav .nav-link:focus {
    color: #1f6fa5;
    border-color: transparent;
    border-bottom-color: #b9d9ee;
}

.relation-tabs >>> .relation-tabs-nav .nav-link.active {
    color: #1f6fa5;
    font-weight: 700;
    background: transparent;
    border-bottom-color: #2a7fb8;
}

.relation-tabs >>> .relation-tabs-content {
    padding: 24px;
}

.tab-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-left: 6px;
    border-radius: 50%;
    background: #dc3545;
    vertical-align: middle;
}

.relation-toggle {
    display: flex;
    width: 100%;
    max-width: 260px;
}

.relation-toggle >>> .btn {
    flex: 1 1 0;
    color: #495057;
    background: #fff;
    border: 1px solid #ced4da;
}

.relation-toggle >>> .btn.active,
.relation-toggle >>> .btn:focus-within {
    color: #3292d0;
    background: #e8f5ff;
    border-color: #3292d0;
}

.relation-actions {
    display: flex;
    justify-content: flex-end;
    padding: 1rem 1.25rem;
    border-top: 1px solid #e9ecef;
}

.relation-actions .btn + .btn {
    margin-left: 0.5rem;
}

.relation-actions-spacer {
    display: none;
}

@media (max-width: 575.98px) {
    /* 分頁列單行、可左右滑動 */
    .relation-tabs >>> .relation-tabs-nav {
        flex-wrap: nowrap;
        overflow-x: auto;
        overflow-y: hidden;
        padding: 0 8px;
        -webkit-overflow-scrolling: touch;
    }

    .relation-tabs >>> .relation-tabs-nav .nav-item {
        flex: 0 0 auto;
        white-space: nowrap;
    }

    .relation-tabs >>> .relation-tabs-nav .nav-link {
        padding: 14px 12px 12px;
    }

    .relation-tabs >>> .relation-tabs-content {
        padding: 16px;
    }

    .relation-required-hint {
        display: none;
    }

    /* 輸入框 44px / 16px，避免 iOS 點擊時自動放大 */
    .relation-form-card >>> .form-control,
    .relation-form-card >>> .custom-select {
        height: 44px;
        font-size: 16px;
    }

    .relation-toggle {
        max-width: none;
    }

    .relation-toggle >>> .btn {
        min-height: 44px;
        line-height: 32px;
        font-size: 16px;
    }

    /* 底部固定操作列：取消:儲存約 1:2 */
    .relation-actions {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1030;
        padding: 0.75rem 1rem;
        background: #fff;
        border-top: 1px solid #dee2e6;
        box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
    }

    .relation-actions .btn {
        min-height: 44px;
    }

    .relation-actions-cancel {
        flex: 1 1 0;
    }

    .relation-actions-save {
        flex: 2 1 0;
    }

    .relation-actions-spacer {
        display: block;
        height: 76px;
    }
}
</style>
